/**
 * Client-side image compression for bill uploads.
 *
 * Why: Cloudflare has a hard ~100s edge timeout. Large phone-camera images
 * (8-15 MB) take too long to (a) upload over mobile networks, and (b) then
 * be processed by Claude OCR. The user then sees a scary Cloudflare 524
 * error and their upload fails.
 *
 * What: Silently compress images in the browser before they're queued for
 * upload. PDFs and small images pass through untouched.
 *
 * Rules (silent, no user prompts):
 *   - PDFs                       → pass-through
 *   - Non-image files            → pass-through
 *   - Image < SKIP_BELOW_BYTES   → pass-through (already small enough)
 *   - Image ≥ SKIP_BELOW_BYTES   → downscale to MAX_DIMENSION and re-encode
 *                                  at JPEG quality QUALITY. If the resulting
 *                                  blob is somehow LARGER than the original
 *                                  (rare, tiny well-compressed JPEGs), keep
 *                                  the original.
 *
 * Notes:
 *   - We keep the aspect ratio; only the longest side is capped at
 *     MAX_DIMENSION px. 2000px is enough for Claude OCR to read printed
 *     bill text reliably while keeping payload small.
 *   - We always output JPEG. PNGs of bills waste bytes; JPEG at 0.82 is
 *     visually indistinguishable and typically 5-10x smaller.
 *   - HEIC / HEIF are not universally supported by the browser's Image
 *     decoder. If decode fails, we return the original file untouched so
 *     the backend can still try (some phones ship HEIC via share-target).
 */

const MAX_DIMENSION = 2000;       // px — longest edge cap
const QUALITY = 0.82;             // JPEG quality (0-1)
const SKIP_BELOW_BYTES = 2 * 1024 * 1024;  // 2 MB — don't bother compressing small files
const OUTPUT_MIME = 'image/jpeg';

/**
 * Load a File into an HTMLImageElement using createObjectURL. We prefer
 * this over `createImageBitmap` because it has broader browser support
 * (older iOS Safari, in-app browsers) and lets us handle EXIF-oriented
 * photos via natural width/height.
 */
function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(url);
            reject(e);
        };
        img.src = url;
    });
}

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), mime, quality);
    });
}

/**
 * Compress a single File. Returns a (possibly new) File. NEVER throws — on
 * any failure we fall back to the original file so uploads still work.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImageFile(file) {
    try {
        if (!file || !file.type) return file;
        if (file.type === 'application/pdf') return file;
        if (!file.type.startsWith('image/')) return file;
        if (file.size < SKIP_BELOW_BYTES) return file;

        const img = await loadImage(file);
        const { naturalWidth: w0, naturalHeight: h0 } = img;
        if (!w0 || !h0) return file;

        // Compute new dimensions, capping the longest edge.
        const longest = Math.max(w0, h0);
        const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
        const w = Math.round(w0 * scale);
        const h = Math.round(h0 * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        // White background — bills are printed on white paper; this avoids
        // black/transparent artifacts if we ever compress a PNG with alpha.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const blob = await canvasToBlob(canvas, OUTPUT_MIME, QUALITY);
        if (!blob) return file;

        // If somehow the "compressed" version is larger, keep the original.
        if (blob.size >= file.size) return file;

        // Preserve original name but swap extension to .jpg so the backend
        // sees a consistent content-type ↔ filename pairing.
        const base = (file.name || 'bill').replace(/\.[a-zA-Z0-9]+$/, '');
        const out = new File([blob], `${base}.jpg`, {
            type: OUTPUT_MIME,
            lastModified: file.lastModified || Date.now(),
        });
        return out;
    } catch (e) {
        // Never block the upload path over a compression failure.
        // eslint-disable-next-line no-console
        console.debug('[imageCompress] failed, falling back to original:', e);
        return file;
    }
}

/**
 * Compress a batch of Files in parallel. Order is preserved.
 * @param {File[] | FileList} files
 * @returns {Promise<File[]>}
 */
export async function compressImageFiles(files) {
    const arr = Array.from(files || []);
    if (!arr.length) return arr;
    return Promise.all(arr.map(compressImageFile));
}

/**
 * Compute the SHA-256 of the ORIGINAL file bytes (before any client-side
 * compression). Returned as a lowercase hex string.
 *
 * Why the "original" and not the "about-to-be-uploaded" bytes?
 * ----------------------------------------------------------------
 * Sprint 2026-09 (dedup regression): the browser's canvas.toBlob() JPEG
 * encoder is NOT bit-deterministic. Uploading the same photo twice
 * produced two different byte streams, both had different SHA256, so the
 * backend's file-hash dedup missed and the bill saved 3 times. By hashing
 * the *original* file we get a stable identifier regardless of whether
 * the client re-encoded before sending — and the backend can dedup on
 * that stable hash before spending an OCR call.
 *
 * Falls back to '' on unsupported browsers so the flow still works
 * (backend still has fallback content-level dedup, this is belt-and-braces).
 *
 * @param {Blob} file
 * @returns {Promise<string>} 64-char hex SHA-256, or '' on failure
 */
export async function computeFileHash(file) {
    try {
        if (!file || !file.arrayBuffer || !window.crypto?.subtle) return '';
        const buf = await file.arrayBuffer();
        const digest = await window.crypto.subtle.digest('SHA-256', buf);
        const bytes = new Uint8Array(digest);
        let hex = '';
        for (let i = 0; i < bytes.length; i++) {
            const h = bytes[i].toString(16);
            hex += h.length === 1 ? '0' + h : h;
        }
        return hex;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.debug('[imageCompress] computeFileHash failed:', e);
        return '';
    }
}
